import { Box, BoxProps, Divider } from "@mui/material";
import React from "react";
import { DefaultLoader } from "../loading/DefaultLoader";
import makeStyles from "@mui/styles/makeStyles";
import clsx from "clsx";
import ClientExerciseListItem, {
  IClientExerciseListItem,
} from "./ClientExerciseListItem";
import { LoadMoreButton } from "../button/LoadMoreButton";
import {
  BodypartType,
  ExerciseAssetDto,
  MovementPatternType,
} from "@growth-machine-llc/stridist-api-client";
import { UserExerciseResultsEmpty } from "./UserExerciseResultsEmpty";
import { useClientExercises } from "./ClientExercisesContext";

export interface ClientExercisesListProps
  extends Omit<BoxProps, "onClick">,
    Pick<IClientExerciseListItem, "onClick"> {
  pageSize?: number;
  query?: string;
  selectedExerciseId?: number;
  bodyparts?: BodypartType[];
  movementPatterns?: MovementPatternType[];
  clientId: number;
  tagIds?: number[];
}

const CLIENT_EXERCISES_LIST_QUERY_KEY = "client-exercises-list";
const CLIENT_EXERCISES_PAGE_SIZE = 12;

const useStyles = makeStyles((theme) => ({
  root: {
    overflowY: "auto",
    height: "100%",
    borderLeft: `1px solid ${theme.palette.divider}`,
    borderRight: `1px solid ${theme.palette.divider}`,
    borderBottom: `1px solid ${theme.palette.divider}`,
    borderRadius: "0 0 10px 10px",
  },

  divider: {
    backgroundColor: theme.palette.quote,
  },

  subHeader: {
    textTransform: "uppercase",
    fontWeight: 700,
  },

  moreButton: {
    marginTop: theme.spacing(1.5),
  },

  loading: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },

  selected: {
    backgroundColor: theme.palette.action.selected,
    "&:hover": {
      backgroundColor: theme.palette.action.selected,
    },
  },
}));

const FETCH_NEXT_PAGE_WHEN_REMAINING = 4;

const isExerciseObsolete = (
  userExerciseTitle: string,
  exerciseAsset: ExerciseAssetDto,
) => {
  return (
    userExerciseTitle.toLowerCase() !== exerciseAsset.name.toLowerCase() ||
    !!exerciseAsset.deleted
  );
};

const ClientExercisesList = (props: ClientExercisesListProps) => {
  const {
    pageSize = CLIENT_EXERCISES_PAGE_SIZE,
    query = "",
    onClick,
    className,
    selectedExerciseId,
    bodyparts,
    movementPatterns,
    clientId,
    tagIds,
    ...other
  } = props;

  const s = useStyles();
  const {
    exercises,
    isLoadingInitial,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    componentListElementRef,
  } = useClientExercises();

  const [initialized, setInitialized] = React.useState(false);

  React.useEffect(() => {
    if (!initialized && exercises.length > 0 && !selectedExerciseId) {
      onClick?.(null, exercises[0]);
      setInitialized(true);
    }
  }, [exercises, initialized, onClick, selectedExerciseId]);

  if (isLoadingInitial) {
    return <DefaultLoader className={clsx(s.root, className)} />;
  }
  if (!exercises.length) {
    return <UserExerciseResultsEmpty className={clsx(s.root, className)} />;
  }

  return (
    <Box className={clsx(s.root, className)} {...other}>
      {exercises?.length > 0 ? (
        <>
          {exercises.map((userExercise, index) => (
            <>
              {index !== 0 && <Divider className={s.divider} />}
              <ClientExerciseListItem
                userExercise={userExercise}
                onClick={onClick}
                isObsolete={isExerciseObsolete(
                  userExercise.title,
                  userExercise.exercise,
                )}
                className={clsx({
                  [s.selected]:
                    selectedExerciseId &&
                    userExercise.id === selectedExerciseId,
                })}
              />
              {index === exercises.length - FETCH_NEXT_PAGE_WHEN_REMAINING && (
                <div ref={componentListElementRef} />
              )}
            </>
          ))}

          {hasNextPage && (
            <LoadMoreButton
              onClick={() => fetchNextPage()}
              loading={isFetchingNextPage}
              disabled={isLoadingInitial}
            />
          )}
        </>
      ) : (
        <Box className={s.loading}>
          <DefaultLoader fillParent size="large" />
        </Box>
      )}
    </Box>
  );
};

export default ClientExercisesList;
